import { cn } from "@/lib/utils";
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
export interface DialogProps {
  modalClass?: string;
  isOpen: boolean;
  onClose: () => void;
  hasBackdropClose?: boolean;
  hasEscClose?: boolean;
  bgClass?: string;
  children?: React.ReactNode;
}

// We'll expose the modal container div ref
export interface DialogRef {
  modalElement: HTMLDivElement | null;
}

const Dialog = forwardRef<DialogRef, DialogProps>((props, ref) => {
  const {
    isOpen,
    onClose,
    children,
    hasBackdropClose,
    hasEscClose,
    modalClass,
    bgClass,
  } = props;

  const backdropRef = useRef<HTMLDivElement>(null);
  const modalContainerRef = useRef<HTMLDivElement>(null);

  // Expose modal container element through ref
  useImperativeHandle(ref, () => ({
    modalElement: modalContainerRef.current,
  }));

  useEffect(() => {
    if (isOpen && backdropRef.current) {
      backdropRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (
        event.code === "Escape" &&
        (event.target === document.body ||
          backdropRef.current === document.activeElement) &&
        isOpen &&
        hasEscClose
      ) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen, onClose, hasEscClose]);

  const handleBackdropClick = () => {
    if (hasBackdropClose) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={cn(
        "fixed top-0 left-0 w-full h-full flex items-center justify-center z-[1100] bg-black bg-opacity-10 overflow-hidden",
        bgClass
      )}
      tabIndex={-1}
      ref={backdropRef}
      onClick={handleBackdropClick}
    >
      <div
        ref={modalContainerRef}
        className={cn(
          "overflow-auto max-h-full max-w-full bg-white !relative",
          modalClass
        )}
        onClick={(e) => e.stopPropagation()} // prevent closing when clicking inside modal
      >
        {children}
      </div>
    </div>
  );
});

Dialog.displayName = "Dialog";

export default Dialog;

